import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { generateId, DESTINATION_EMOJIS, TRIP_TYPES } from '../utils/helpers';

const emptyHoliday = {
  destination: '',
  emoji: '🏖️',
  tripType: 'holiday',
  departureDate: '',
  returnDate: '',
  status: 'booked',
  totalBudget: 0,
  paymentDeadline: '',
  familyMembers: [],
  expenses: [],
  notes: '',
  outboundFlight: '',
  returnFlight: '',
};

export default function HolidayForm({ isOpen, onClose, onSave, holiday, members }) {
  const [form, setForm] = useState(emptyHoliday);

  useEffect(() => {
    if (holiday) {
      setForm(holiday);
    } else {
      setForm({ ...emptyHoliday, id: generateId(), createdAt: new Date().toISOString() });
    }
  }, [holiday, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.destination || !form.departureDate) return;
    onSave(form);
    onClose();
  };

  const toggleMember = (id) => {
    setForm(f => ({
      ...f,
      familyMembers: f.familyMembers.includes(id)
        ? f.familyMembers.filter(m => m !== id)
        : [...f.familyMembers, id],
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={holiday ? 'Edit Trip' : 'Add Trip'} wide>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Destination */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Destination *</label>
          <input
            type="text"
            value={form.destination}
            onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
            placeholder="Where are you going?"
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
            required
          />
        </div>

        {/* Emoji picker */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Choose an emoji</label>
          <div className="flex flex-wrap gap-2">
            {DESTINATION_EMOJIS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => setForm(f => ({ ...f, emoji }))}
                className={`text-2xl w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                  form.emoji === emoji ? 'bg-purple-100 ring-2 ring-purple-400 scale-110' : 'hover:bg-gray-100'
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Trip type */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Trip type</label>
          <div className="grid grid-cols-2 gap-2">
            {TRIP_TYPES.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm(f => ({ ...f, tripType: t.id }))}
                className={`py-2 px-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  form.tripType === t.id
                    ? 'bg-purple-50 border-purple-300 text-purple-700'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Departure *</label>
            <input
              type="date"
              value={form.departureDate}
              onChange={e => setForm(f => ({ ...f, departureDate: e.target.value }))}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Return</label>
            <input
              type="date"
              value={form.returnDate}
              onChange={e => setForm(f => ({ ...f, returnDate: e.target.value }))}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* Flight details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Outbound Flight</label>
            <input
              type="text"
              value={form.outboundFlight || ''}
              onChange={e => setForm(f => ({ ...f, outboundFlight: e.target.value.toUpperCase() }))}
              placeholder="e.g. FR1234"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Return Flight</label>
            <input
              type="text"
              value={form.returnFlight || ''}
              onChange={e => setForm(f => ({ ...f, returnFlight: e.target.value.toUpperCase() }))}
              placeholder="e.g. FR1235"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* Total cost & payment deadline */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Total Cost (£)</label>
            <div className="relative">
              <span className="absolute left-4 top-2.5 text-gray-400">£</span>
              <input
                type="number"
                value={form.totalBudget || ''}
                onChange={e => setForm(f => ({ ...f, totalBudget: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
                className="w-full pl-8 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Deadline</label>
            <input
              type="date"
              value={form.paymentDeadline || ''}
              onChange={e => setForm(f => ({ ...f, paymentDeadline: e.target.value }))}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
          <div className="flex gap-2">
            {[
              { value: 'booked', label: '✅ Booked', bg: 'bg-green-50 border-green-300 text-green-700' },
              { value: 'planning', label: '📋 Planning', bg: 'bg-blue-50 border-blue-300 text-blue-700' },
              { value: 'dream', label: '✨ Dream Trip', bg: 'bg-sunny-50 border-sunny-300 text-sunny-700' },
            ].map(s => (
              <button
                key={s.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, status: s.value }))}
                className={`flex-1 py-2 px-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  form.status === s.value ? s.bg : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Family members */}
        {members.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Family members</label>
            <div className="flex flex-wrap gap-2">
              {members.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleMember(m.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    form.familyMembers.includes(m.id)
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                  style={form.familyMembers.includes(m.id) ? { backgroundColor: m.colour } : {}}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Any details, booking references, excitement..."
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors resize-none"
            rows={3}
          />
        </div>

        {/* Submit */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 border-2 border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-2.5 px-4 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white rounded-xl font-semibold transition-colors"
          >
            {holiday ? 'Save Changes' : 'Add Trip 🌴'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
